import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Button,
  Group,
  Paper,
  TextInput,
  Table,
  Pagination,
  Loader,
  Center,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconRefresh } from "@tabler/icons-react";
import { usePermissionsAdmin } from "../../../hooks/admin/usePermissions";

const ITEMS_PER_PAGE = 20;

export default function PermissionsAdminPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const { data: permissions = [], isLoading, refetch } = usePermissionsAdmin();

  const filteredPermissions = useMemo(() => {
    if (!debouncedSearch) return permissions;
    const searchLower = debouncedSearch.toLowerCase();
    return permissions.filter(
      (permission) =>
        permission.name.toLowerCase().includes(searchLower) ||
        permission.description?.toLowerCase().includes(searchLower)
    );
  }, [permissions, debouncedSearch]);

  const totalPages = Math.ceil(filteredPermissions.length / ITEMS_PER_PAGE);
  const paginatedPermissions = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredPermissions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPermissions, page]);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Permissions</Title>
              <Text size="sm" c="dimmed" mt="xs">
                System permissions are code-defined and managed by developers
                via database migrations.
              </Text>
            </div>
          </Group>

          {/* Search Section */}
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <Button
                  variant="subtle"
                  size="compact-sm"
                  onClick={() => refetch()}
                  loading={isLoading}
                  leftSection={<IconRefresh size={16} />}
                >
                  Refresh
                </Button>
              </Group>
              <TextInput
                placeholder="Search by name or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
              />
              <Text size="sm" c="dimmed">
                Showing {filteredPermissions.length} of {permissions.length}{" "}
                permissions
              </Text>
            </Stack>
          </Paper>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Permissions ({filteredPermissions.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredPermissions.length
                      )}{" "}
                      of {filteredPermissions.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredPermissions.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search
                    ? "No permissions match the search"
                    : "No permissions found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Permission Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Created</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedPermissions.map((permission) => (
                      <Table.Tr key={permission.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {permission.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {permission.description || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(
                              permission.createdAt
                            ).toLocaleDateString()}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Container>
  );
}
